import classnames from 'classnames'
import { useField } from 'formik'
import { type SelectComponents } from 'node_modules/react-select/dist/declarations/src/components'
import * as React from 'react'
import { type GroupBase } from 'react-select'
import { useTranslatable } from 'src/hooks/locale/utils'
import AsyncSelect from '../../AsyncSelect'
import { useFormData } from './Form'
import { type Option } from './SelectInput'

interface Props {
  readonly loadOptions: (inputValue: string) => Promise<readonly Option[]>
  readonly placeholder?: string
  readonly className?: string
  readonly label?: string
  readonly name: string
  readonly required?: boolean
  readonly disabled?: boolean
  readonly isMulti?: boolean
  readonly labelAnimation?: boolean
  readonly components?: Partial<SelectComponents<any, boolean, GroupBase<any>>> | undefined
  readonly instructions?: React.ReactElement
  readonly closeMenuOnSelect?: boolean
  readonly hideSelectedOptions?: boolean
  readonly menuPortalTarget?: HTMLElement
  readonly getOptionLabel?: (option: { value: string; label: string }) => string
  readonly getOptionValue?: (option: { value: string; label: string }) => string
  readonly storeValueOnly?: boolean
  readonly cacheOptions?: boolean
  readonly defaultOptions?: boolean | readonly Option[]
  readonly initialKnownLabels?: Readonly<Record<string, string>>
}

export default function AsyncSelectInput({
  label,
  labelAnimation = false,
  components,
  instructions,
  required,
  closeMenuOnSelect = true,
  hideSelectedOptions = false,
  menuPortalTarget = document.body,
  getOptionLabel = undefined,
  getOptionValue = undefined,
  storeValueOnly = false,
  cacheOptions = true,
  defaultOptions,
  loadOptions,
  initialKnownLabels,
  ...props
}: Props): React.ReactElement {
  const t = useTranslatable()
  const [field, meta, helpers] = useField({
    ...props,
    validate(value) {
      if (required === true) {
        if (value == null || value === '' || value === undefined || ((props.isMulti ?? false) && value[0] == null)) {
          return label !== undefined ? `${label} ${t('common:is_required')}` : t('common:field_is_required')
        }
      }
    },
  })

  const { setValue, setTouched } = helpers
  const [formData] = useFormData()
  const errors = formData.errors.fields[props.name] ?? []
  const isInvalid = (errors.length > 0 || meta.error != null) && meta.touched ? '!border-primaryRed' : ''
  const [focused, setFocused] = React.useState(false)

  const [knownLabels, setKnownLabels] = React.useState<Readonly<Record<string, string>>>(initialKnownLabels ?? {})

  const mergeLabelsFromOptions = React.useCallback((opts: readonly Option[]) => {
    if (opts.length === 0) return
    setKnownLabels((prev) => {
      let changed = false
      const next: Record<string, string> = { ...prev }
      for (const o of opts) {
        if (next[o.value] !== o.label) {
          next[o.value] = o.label
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [])

  const wrappedLoadOptions = React.useCallback(
    async (inputValue: string) => {
      const opts = await loadOptions(inputValue)
      mergeLabelsFromOptions(opts)
      return [...opts]
    },
    [loadOptions, mergeLabelsFromOptions]
  )

  const handleChange = React.useCallback(
    (option: { value: string; label: string } | readonly { value: string; label: string }[] | null) => {
      if (props.isMulti ?? false) {
        const arr = option == null ? [] : Array.isArray(option) ? option : [option]
        const filtered = arr as readonly { value: string; label: string }[]
        mergeLabelsFromOptions(filtered)
        void setValue(storeValueOnly ? filtered.map((o) => o.value) : filtered)
      } else {
        const single = option as { value: string; label: string } | null
        if (single != null) mergeLabelsFromOptions([single])
        void setValue(single?.value)
      }
    },
    [props.isMulti, mergeLabelsFromOptions, storeValueOnly, setValue]
  )

  const getValue = (): any => {
    if (props.isMulti ?? false) {
      if (storeValueOnly) {
        const values = Array.isArray(field.value) ? field.value : field.value ? [field.value] : []
        return values.map((code: string) => ({
          value: code,
          label: knownLabels[code] ?? code,
        }))
      }
      return Array.isArray(field.value) ? field.value : []
    }
    if (storeValueOnly) {
      const v = field.value as string | undefined
      if (v == null || v === '') return null
      return { value: v, label: knownLabels[v] ?? v }
    }
    return field.value
  }

  return (
    <div className="mb-3 w-full">
      <div
        className={classnames('relative w-full', {
          labelAnimation,
        })}
        data-testid={props.name}
      >
        {label != null && (
          <label
            className={`${
              labelAnimation
                ? `absolute left-[12px] z-10 flex-nowrap text-lightPrimaryIconText duration-200 ease-in-out ${
                    (field.value != null && field.value !== '') || focused
                      ? 'top-[5%] translate-y-[-10%] text-[11px]'
                      : 'top-1/2 -translate-y-1/2'
                  }`
                : 'mb-[10px] flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor'
            }`}
            htmlFor={props.name}
          >
            {label}{' '}
            {required != null && (
              <span className="pl-1 text-lightSecondaryWarning" data-testid={`${label ?? props.placeholder}.required`}>
                *
              </span>
            )}{' '}
            {instructions}
          </label>
        )}
        <AsyncSelect
          placeholder={
            props.placeholder != null && !labelAnimation
              ? props.placeholder
              : props.placeholder == null && !labelAnimation
                ? t('common:select')
                : ''
          }
          className={`p-0 ${isInvalid} ${props.className}`}
          name={field.name}
          value={getValue()}
          onChange={(option) =>
            handleChange(
              option as { value: string; label: string } | readonly { value: string; label: string }[] | null
            )
          }
          onFocus={() => setFocused(true)}
          onBlur={() => {
            void setTouched(true, true)
            setFocused(false)
          }}
          instanceId={props.name}
          hasError={
            meta.error != null &&
            meta.touched &&
            (required === true
              ? (Array.isArray(field.value) && field.value.length === 0) ||
                field.value === '' ||
                field.value === undefined
              : true)
          }
          inputId={props.name}
          isDisabled={props.disabled === true}
          noOptionsMessage={() => t('common:no_options')}
          isMulti={props.isMulti}
          components={components}
          closeMenuOnSelect={closeMenuOnSelect}
          hideSelectedOptions={hideSelectedOptions}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          loadOptions={wrappedLoadOptions}
          cacheOptions={cacheOptions}
          {...(defaultOptions !== undefined ? { defaultOptions } : {})}
          {...(menuPortalTarget != null && { menuPortalTarget })}
        />
      </div>
      {meta.error != null &&
      meta.touched &&
      (required === true
        ? (Array.isArray(field.value) && field.value.length === 0) || field.value === '' || field.value === undefined
        : true) ? (
        <div className="mt-2 text-[13px] text-errorMessages">{meta.error}</div>
      ) : null}
      {errors.map((error, i) => (
        <div key={i} className="mt-2 text-[13px] text-errorMessages">
          {error}
        </div>
      ))}
    </div>
  )
}
